import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

type SidebarContextType = {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
};

const SidebarContext = createContext<SidebarContextType>({
  isOpen: false,
  toggle: () => null,
  close: () => null,
});

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Initialize with closed state on mobile, open on desktop
  useEffect(() => {
    setIsMounted(true);
    const isDesktop = window.innerWidth >= 1024;
    setIsOpen(isDesktop);

    // Close sidebar when resizing to desktop
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      if (isDesktop) {
        setIsOpen(true);
      } else if (!isDesktop && !isMounted) {
        // Only auto-close on initial load for mobile
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMounted]);

  const toggle = () => setIsOpen((prev) => !prev);
  const close = () => setIsOpen(false);

  return <SidebarContext.Provider value={{ isOpen, toggle, close }}>{children}</SidebarContext.Provider>;
};

export const useSidebar = () => useContext(SidebarContext);
